import React, { useState, useContext, useEffect } from 'react'
import { Paper, Grid, ButtonBase, Typography } from '@material-ui/core'
import { Apple, ErrorOutline } from '@material-ui/icons'
import CreditCard from './PaymentMethods/CreditCard'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import { PAYMENT_METHODS } from '../../Constants'
import { isInFlow } from '../../services/utils'
import { GooglePayIcon, DebitIcon } from '../Core/Icons/PaymentIcons.js'
import { makeStyles } from '@material-ui/styles'
import { SessionService } from '../../services/SessionService.js'
import { UserApi } from '../../api/user.api.js'
import { useLocation } from 'react-router-dom'
import WalletPayment from './PaymentMethods/WalletPayment'
import { InsurancePurchaseContextStore } from '../../context/InsuracePurchaseContext'
import { SessionContextStore } from '../../context/SessionContext'
import DiscountsService from '../../services/DiscountsService'
import { BuyingInsuranceContextStore } from '../../context/BuyingInsuranceContext'

const usePaperStyle = makeStyles((theme) => ({
  root: {
    '&.checked': {
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.primary.superLight,
    },
    '&:hover': {
      cursor: 'pointer',
    },
  },
}))

const useRippleStyle = makeStyles((theme) => ({
  root: {
    '&': {
      width: '100%',
      display: 'block',
    },
  },
}))

const PaymentIcon = ({ paymentMethod }) => {
  switch (paymentMethod) {
    case 'credit':
      return <DebitIcon />
    case 'applePay':
      return <Apple />
    case 'googlePay':
      return <GooglePayIcon />
    default:
      return <GooglePayIcon />
  }
}

const PaymentMethods = ({ onPurchase }) => {
  const stripe = useStripe()
  const elements = useElements()
  const location = useLocation()
  const paperClasses = usePaperStyle()
  const rippleClasses = useRippleStyle()
  const offersContext = useContext(BuyingInsuranceContextStore)
  const sessionContext = useContext(SessionContextStore)
  const [wallet, setWallet] = useState()
  const [paymentRequest, setPaymentRequest] = useState(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('credit')
  const [availableMethods, setAvailableMethods] = useState({ credit: true })

  useEffect(() => {
    if (wallet) setAvailableMethods({ ...availableMethods, [wallet]: true })
  }, [wallet])

  useEffect(() => {
    if (!stripe || !elements) return
    if (SessionService.isBroker()) return

    if (!isInFlow(location.pathname)) return

    let total = parseInt(offersContext.pricebreakdown.price.toFixed(2) * 100)

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Total',
        amount: total,
      },
      requestPayerEmail: true,
    })

    pr.canMakePayment().then((result) => {
      if (!result) return
      setPaymentRequest(pr)
      setWallet(
        result.googlePay ? 'googlePay' : result.applePay ? 'applePay' : null
      )
    })
  }, [stripe, elements])

  useEffect(() => {
    if (!paymentRequest) return
    paymentRequest.on('paymentmethod', async (e) => {
      let method = 'Google/Apple'
      switch (e.walletName) {
        case 'googlePay':
          method = 'Google-Pay'
          break
        case 'applePay':
          method = 'Apple-Pay'
          break
      }
      let response = await UserApi.attachPaymentMethod(
        e.paymentMethod.id,
        false,
        method
      )
      if (response.ok) {
        e.complete('success')
        onPurchase()
        return
      }
      e.complete('fail')
    })
  }, [paymentRequest])

  const handleChangePaymentMethod = (e, method) => {
    setSelectedPaymentMethod(method)
    methodToFunction(method)
  }

  const renderPaymentMethod = () => {
    switch (selectedPaymentMethod) {
      case 'googlePay':
      case 'applePay':
        return <WalletPayment methodOfPayment={selectedPaymentMethod} />
      case 'credit':
        return (
          <CreditCard
            stripe={stripe}
            elements={elements}
            onPurchase={onPurchase}
          />
        )
      default:
        break
    }
  }

  const methodToFunction = (method) => {
    if (!paymentRequest) return
    switch (method) {
      case 'applePay':
      case 'googlePay':
        paymentRequest.show()
        break
      default:
        break
    }
  }

  return (
    <Grid container>
      <Grid
        container
        item
        xs={12}
        sm={7}
        spacing={1}
        className="margin-bottom-basic">
        {Object.keys(availableMethods).map((method) => {
          const hideMethod = !availableMethods[method]
          return (
            <Grid
              item
              xs={4}
              sm={3}
              key={method}
              hidden={hideMethod}
              onClick={(e) => handleChangePaymentMethod(e, method)}>
              <ButtonBase className={rippleClasses.root}>
                <Paper
                  className={`${
                    method === selectedPaymentMethod ? 'checked' : ''
                  } ${paperClasses.root} padding-top-10 padding-bottom-10`}
                  elevation={0}
                  variant="outlined"
                  key={method}>
                  <Grid container justifyContent="center">
                    <PaymentIcon paymentMethod={method} />
                  </Grid>
                  <Grid
                    container
                    justifyContent="center"
                    className="margin-top-6">
                    <span
                      className={`${
                        method === selectedPaymentMethod ? 'blue' : 'gray'
                      } bold text-small payment-method-btn-text`}>
                      {PAYMENT_METHODS[method].name}
                    </span>
                  </Grid>
                </Paper>
              </ButtonBase>
            </Grid>
          )
        })}
      </Grid>
      <Grid container>{renderPaymentMethod()}</Grid>
    </Grid>
  )
}

export default PaymentMethods
